import { useState, useEffect } from 'react';
import { Lock, CheckCircle, XCircle } from 'lucide-react';

export function ResetPasswordPage({ onResetComplete }: { onResetComplete?: () => void }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [validToken, setValidToken] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('token');

    if (resetToken) {
      setToken(resetToken);
      setValidToken(true);
    } else {
      setError('Lien de réinitialisation invalide ou expiré');
    }
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!password || !confirmPassword) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);

    if (!hasUpperCase || !hasLowerCase || !hasNumber) {
      setError('Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre');
      return;
    }

    if (!token) {
      setError('Token manquant');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-reset-token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: token,
            newPassword: password,
          }),
        }
      );

      const data = await response.json();

      setLoading(false);

      if (!response.ok) {
        setError(data.error || 'Une erreur est survenue. Veuillez réessayer.');
        console.error('Update password error:', data);
      } else {
        setSuccess(true);
        setTimeout(() => {
          if (onResetComplete) {
            onResetComplete();
          }
        }, 3000);
      }
    } catch (error) {
      setLoading(false);
      setError('Une erreur est survenue. Veuillez réessayer.');
      console.error('Update password error:', error);
    }
  }

  if (!validToken && !error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Vérification du lien...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-green-700 p-8 text-white">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-white/20 p-3 rounded-lg">
                <CheckCircle size={32} />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-center mb-2">Mot de passe modifié</h1>
            <p className="text-green-100 text-center text-sm">
              Votre mot de passe a été mis à jour avec succès
            </p>
          </div>
          <div className="p-8 text-center">
            <p className="text-gray-600 mb-4">
              Vous allez être redirigé vers la page de connexion...
            </p>
            <button
              onClick={onResetComplete}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all"
            >
              Se connecter maintenant
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error && !validToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          <div className="bg-gradient-to-r from-red-600 to-red-700 p-8 text-white">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-white/20 p-3 rounded-lg">
                <XCircle size={32} />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-center mb-2">Lien invalide</h1>
            <p className="text-red-100 text-center text-sm">
              Ce lien de réinitialisation est invalide ou a expiré
            </p>
          </div>
          <div className="p-8 text-center">
            <p className="text-gray-600 mb-4">
              Veuillez demander un nouveau lien de réinitialisation depuis la page de connexion.
            </p>
            <button
              onClick={onResetComplete}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all"
            >
              Retour à la connexion
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-8 text-white">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white/20 p-3 rounded-lg">
              <Lock size={32} />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-center mb-2">Nouveau mot de passe</h1>
          <p className="text-blue-100 text-center text-sm">
            Choisissez un nouveau mot de passe sécurisé
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-8">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Lock size={16} className="inline mr-2" />
                Nouveau mot de passe
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                placeholder="••••••••"
                required
              />
              <p className="mt-2 text-xs text-gray-500">
                Au moins 8 caractères avec majuscule, minuscule et chiffre
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Lock size={16} className="inline mr-2" />
                Confirmer le mot de passe
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Chargement...' : 'Réinitialiser le mot de passe'}
          </button>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={onResetComplete}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
            >
              Retour à la connexion
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
